<script setup lang="ts">
import {VisitMetrics} from "@/core/http/resources/MetricsResource";
import {Table, TableBody, TableCell, TableHeader, TableRow} from "@/core/components/ui/table";
import {unique} from "@/core/helpers/array";
import PatientVisitMetric from "@/patient-administration/components/PatientVisitMetric.vue";

const props = defineProps<{
    metrics: {
        local: VisitMetrics[],
        global: VisitMetrics[],
    }
}>();

/** translations for source data. */
const translations = {
    pathology: {
        diabetics: "Diabétiques",
        asthma: "Asthme",
        cardio: "Cardiaques"
    },
    gender: {
        male: "Homme",
        female: "Femme"
    }
};

/** The pathology labels. */
const pathologies = (metrics: VisitMetrics[]): string[] => {
    return unique(metrics.map((metric) => metric.pathology)) as string[];
};

/** Group by age. */
const groupByAge = (metrics: VisitMetrics[], pathology: string): Record<string, VisitMetrics[]> => {
    return Object.groupBy(metrics.filter(m => m.pathology === pathology), (metric) => metric.age) as Record<string, VisitMetrics[]>;
};

/** Get local gender metric. */
const local = (metrics: VisitMetrics[], gender: string): VisitMetrics => {
    return metrics.find((metric) => metric.gender === gender) as VisitMetrics;
};

/** Get national gender metric of a pathology. */
const national = (pathology: string, age: string, gender: string): VisitMetrics => {
    return props.metrics.global.find((metric) => metric.pathology === pathology && metric.age === age && metric.gender === gender) as VisitMetrics;
};

</script>

<template>
  <div v-for="pathology in pathologies(metrics.local)" :key="pathology" class="py-4 gap-4">
    <h3>{{ translations.pathology[pathology] ?? pathology }}</h3>
    <Table>
      <TableHeader>
        <TableRow class="font-bold">
          <TableCell>Age</TableCell>
          <TableCell>Patients</TableCell>
          <TableCell>Visites</TableCell>
          <TableCell>C.A.</TableCell>
        </TableRow>
      </TableHeader>
      <TableBody>
        <TableRow v-for="metricByAge in groupByAge(metrics.local, pathology)" :key="pathology + metricByAge![0].age">
          <TableCell>{{ metricByAge![0].age }}</TableCell>
          <TableCell>
            <PatientVisitMetric
              gender="male"
              :local="local(metricByAge, 'male').patients"
              :national="national(pathology, metricByAge![0].age, 'male').patients"
            />
            <PatientVisitMetric
              gender="female"
              :local="local(metricByAge, 'female').patients"
              :national="national(pathology, metricByAge![0].age, 'female').patients"
            />
          </TableCell>
          <TableCell>
            <PatientVisitMetric
              gender="male"
              :local="local(metricByAge, 'male').visits"
              :national="national(pathology, metricByAge![0].age, 'male').visits"
            />
            <PatientVisitMetric
              gender="female"
              :local="local(metricByAge, 'female').visits"
              :national="national(pathology, metricByAge![0].age, 'female').visits"
            />
          </TableCell>
          <TableCell>
            <PatientVisitMetric
              gender="male"
              :local="local(metricByAge, 'male').revenue"
              :national="national(pathology, metricByAge![0].age, 'male').revenue"
              :currency="true"
            />
            <PatientVisitMetric
              gender="female"
              :local="local(metricByAge, 'female').revenue"
              :national="national(pathology, metricByAge![0].age, 'female').revenue"
              :currency="true"
            />
          </TableCell>
        </tablerow>
      </TableBody>
    </Table>
  </div>
</template>
